import React, { useState, useEffect, useCallback, useRef, Suspense, lazy } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Check, X, Image, Pencil, Eye, Smile } from "lucide-react";
import { useAuth, API_URL } from "../../AuthContext";
import { SPECIALTIES } from "../../SpecialtyContext";
import { useNavigate } from "react-router-dom";
import BannersTab from "./BannersTab";
import VkTab from "./VkTab";
import SeriesTab from "./SeriesTab";
import StatsTab from "./StatsTab";
import ChatImportTab from "./ChatImportTab";
import SsoTab from "./SsoTab";

// Библиотека эмодзи-пикера подгружается только по клику на кнопку 😀,
// чтобы не увеличивать основной бандл для читателей публичных страниц.
const EmojiPicker = lazy(() => import("emoji-picker-react"));

// ── Markdown-редактор с toolbar и предпросмотром ──────────────────────────

const MD_BUTTONS = [
  { label: "B",  title: "Жирный",     wrap: ["**", "**"],   style: { fontWeight: 900 } },
  { label: "I",  title: "Курсив",     wrap: ["_", "_"],     style: { fontStyle: "italic" } },
  { label: "S",  title: "Зачёркнутый", wrap: ["~~", "~~"],  style: { textDecoration: "line-through" } },
  { label: "H2", title: "Заголовок",  prefix: "## ",        style: {} },
  { label: "H3", title: "Подзаголовок", prefix: "### ",     style: {} },
  { label: "—",  title: "Список",     prefix: "- ",         style: {} },
  { label: "1.", title: "Нумерованный список", prefix: "1. ", style: {} },
  { label: "'", title: "Цитата",      prefix: "> ",         style: {} },
  { label: "`", title: "Код",         wrap: ["`", "`"],     style: { fontFamily: "monospace" } },
  { label: "🔗", title: "Ссылка",     wrap: ["[", "](https://)"], style: {} },
  { label: "▤", title: "Таблица",
    insert: "\n| Заголовок 1 | Заголовок 2 |\n| --- | --- |\n| Ячейка | Ячейка |\n", style: {} },
  { label: "―", title: "Разделитель", insert: "\n---\n", style: {} },
];

function MarkdownEditor({ value, onChange, rows = 10, required, token }) {
  const taRef = useRef(null);
  const fileInputRef = useRef(null);
  const [preview, setPreview] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [showEmoji, setShowEmoji] = useState(false);

  const insertAtCursor = (insertText) => {
    const ta = taRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const newText = value.slice(0, start) + insertText + value.slice(end);
    onChange({ target: { value: newText } });
    setTimeout(() => {
      ta.focus();
      const pos = start + insertText.length;
      ta.setSelectionRange(pos, pos);
    }, 10);
  };

  const insertMarkdown = (btn) => {
    const ta = taRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const sel = value.slice(start, end);
    if (btn.wrap) {
      insertAtCursor(btn.wrap[0] + (sel || "текст") + btn.wrap[1]);
    } else if (btn.insert) {
      insertAtCursor(btn.insert);
    } else {
      const lineStart = value.lastIndexOf("\n", start - 1) + 1;
      const newText = value.slice(0, lineStart) + btn.prefix + value.slice(lineStart);
      onChange({ target: { value: newText } });
      setTimeout(() => { ta.focus(); }, 10);
    }
  };

  const uploadImage = async (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    setUploading(true);
    setUploadError(null);
    const form = new FormData();
    form.append("file", file);
    try {
      const r = await fetch(`${API_URL}/upload/image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const data = await r.json();
      if (r.ok && data.url) {
        insertAtCursor(`![${file.name}](${data.url})`);
      } else {
        setUploadError(data.error || "Не удалось загрузить изображение");
      }
    } catch {
      setUploadError("Ошибка сети при загрузке");
    }
    setUploading(false);
  };

  const handlePaste = (e) => {
    const item = Array.from(e.clipboardData?.items || []).find(i => i.type.startsWith("image/"));
    if (item) {
      e.preventDefault();
      uploadImage(item.getAsFile());
    }
  };

  const handleDrop = (e) => {
    const file = Array.from(e.dataTransfer?.files || []).find(f => f.type.startsWith("image/"));
    if (file) {
      e.preventDefault();
      uploadImage(file);
    }
  };

  return (
    <div className="md-editor">
      <div className="md-toolbar">
        {MD_BUTTONS.map(btn => (
          <button key={btn.label} type="button" title={btn.title}
            className="md-tool-btn" style={btn.style}
            onClick={() => insertMarkdown(btn)}>
            {btn.label}
          </button>
        ))}
        <button type="button" title="Вставить изображение (или перетащите/вставьте из буфера)"
          className="md-tool-btn" disabled={uploading}
          onClick={() => fileInputRef.current?.click()}>
          {uploading ? "⏳" : <Image size={15} strokeWidth={2.1} />}
        </button>
        <input
          ref={fileInputRef} type="file" accept="image/*" style={{ display: "none" }}
          onChange={e => { uploadImage(e.target.files[0]); e.target.value = ""; }}
        />
        <div style={{ position: "relative" }}>
          <button type="button" title="Вставить эмодзи"
            className={`md-tool-btn${showEmoji ? " active" : ""}`}
            onClick={() => setShowEmoji(s => !s)}>
            <Smile size={15} strokeWidth={2.1} />
          </button>
          {showEmoji && (
            <>
              <div onClick={() => setShowEmoji(false)}
                style={{ position: "fixed", inset: 0, zIndex: 19 }} />
              <div style={{ position: "absolute", top: "100%", left: 0, zIndex: 20, marginTop: 4 }}>
                <Suspense fallback={<div style={{ background: "#fff", padding: 16, borderRadius: 8, boxShadow: "0 4px 20px rgba(0,0,0,0.15)" }}>Загрузка…</div>}>
                  <EmojiPicker
                    onEmojiClick={(emojiData) => { insertAtCursor(emojiData.emoji); setShowEmoji(false); }}
                    width={320} height={380}
                  />
                </Suspense>
              </div>
            </>
          )}
        </div>
        <span style={{ flex: 1 }} />
        <button type="button"
          className={`md-tool-btn${preview ? " active" : ""}`}
          onClick={() => setPreview(p => !p)}
          title="Предпросмотр"
          style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          {preview ? <><Pencil size={13} strokeWidth={2.1} /> Редактор</> : <><Eye size={13} strokeWidth={2.1} /> Просмотр</>}
        </button>
      </div>

      {uploadError && (
        <div style={{ background: "#fdecea", color: "#c0392b", fontSize: "0.82rem", padding: "6px 12px" }}>
          {uploadError}
        </div>
      )}

      {preview ? (
        <div className="md-preview article-body">
          {value ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{value}</ReactMarkdown> : <span style={{ color: "#bbb" }}>Начните вводить текст…</span>}
        </div>
      ) : (
        <textarea
          ref={taRef}
          className="admin-textarea"
          value={value}
          onChange={onChange}
          onPaste={handlePaste}
          onDrop={handleDrop}
          onDragOver={e => e.preventDefault()}
          required={required}
          rows={rows}
          placeholder={`Поддерживается Markdown:\n## Заголовок\n**жирный**, _курсив_, ~~зачёркнутый~~\n- пункт списка\n> цитата\n[ссылка](https://...)\n\nИзображение: кнопка 🖼, перетащите файл или вставьте из буфера (Ctrl+V)\nЭмодзи: кнопка 😀`}
          style={{ borderRadius: "0 0 8px 8px", borderTop: "none" }}
        />
      )}
    </div>
  );
}

const TABS = ["Обзор", "Статьи", "Материалы", "События", "Случаи", "Жалобы", "Верификация", "Пользователи", "Редакторы разделов", "Каналы", "Telegram", "ИИ-поиск", "Второе мнение", "Производители", "Баннеры", "ВКонтакте", "Серии", "Статистика", "Единый вход", "Импорт чата"];

// ── Вспомогательные компоненты ────────────────────────────────────────────

function ConfirmBtn({ label, onConfirm, danger = true }) {
  const [ask, setAsk] = useState(false);
  if (ask) return (
    <span style={{ display: "inline-flex", gap: 4 }}>
      <button onClick={onConfirm} style={{ background: "#d9534f", padding: "4px 10px", marginTop: 0 }}>Да</button>
      <button onClick={() => setAsk(false)} style={{ background: "#888", padding: "4px 10px", marginTop: 0 }}>Нет</button>
    </span>
  );
  return (
    <button onClick={() => setAsk(true)}
      style={{ background: danger ? "#d9534f" : "#007c84", padding: "4px 12px", marginTop: 0 }}>
      {label}
    </button>
  );
}

// ── Селектор специальности (для editor — ограничен его собственными специальностями) ──

function SpecialtySelect({ value, onChange, allowedSpecialties }) {
  // Editor с одной специальностью — поле заблокировано (нечего выбирать)
  if (allowedSpecialties && allowedSpecialties.length === 1) {
    const label = SPECIALTIES.find(s => s.id === allowedSpecialties[0])?.label || allowedSpecialties[0];
    return (
      <div className="admin-input" style={{ background: "#f0f2f8", color: "#555", display: "flex", alignItems: "center" }}>
        {label} <span style={{ marginLeft: 6, fontSize: "0.76rem", color: "#999" }}>(ваша специальность)</span>
      </div>
    );
  }
  // Editor с несколькими специальностями — выбор ограничен только его специальностями
  if (allowedSpecialties && allowedSpecialties.length > 1) {
    return (
      <select className="admin-input" value={value || ""} onChange={onChange}>
        {allowedSpecialties.map(id => {
          const label = SPECIALTIES.find(s => s.id === id)?.label || id;
          return <option key={id} value={id}>{label}</option>;
        })}
      </select>
    );
  }
  return (
    <select className="admin-input" value={value || ""} onChange={onChange}>
      <option value="">Все специальности</option>
      {SPECIALTIES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
    </select>
  );
}

// ── Мультивыбор специальностей (для editor с несколькими областями, напр. кардио + функц. диагностика) ──

function SpecialtyMultiSelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = (value || "").split(";").map(s => s.trim()).filter(Boolean);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const toggle = id => {
    const next = selected.includes(id) ? selected.filter(s => s !== id) : [...selected, id];
    onChange(next.join(";") || null);
  };

  const label = selected.length === 0
    ? "— специальности —"
    : selected.map(id => SPECIALTIES.find(s => s.id === id)?.label || id).join(", ");

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ fontSize: "0.82rem", padding: "3px 8px", borderRadius: 6, border: "1px solid #c4ceda",
          background: "#fff", cursor: "pointer", maxWidth: 220, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {label}
      </button>
      {open && (
        <div style={{ position: "absolute", top: "100%", left: 0, marginTop: 4, zIndex: 30,
          background: "#fff", border: "1px solid #dde3f0", borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
          padding: 8, width: 220, maxHeight: 240, overflowY: "auto" }}>
          {SPECIALTIES.map(s => (
            <label key={s.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.85rem", padding: "3px 2px", cursor: "pointer" }}>
              <input type="checkbox" checked={selected.includes(s.id)} onChange={() => toggle(s.id)} />
              {s.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Форма статьи ─────────────────────────────────────────────────────────

function ArticleForm({ initial, onSave, onCancel, token, allowedSpecialties }) {
  const [form, setForm] = useState(initial || { title: "", content: "", tags: "", specialty: allowedSpecialties?.[0] || SPECIALTIES[0].id });
  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }));
  const [saving, setSaving] = useState(false);

  const save = async e => {
    e.preventDefault();
    setSaving(true);
    const method = initial?.id ? "PUT" : "POST";
    const url = initial?.id ? `${API_URL}/posts/${initial.id}` : `${API_URL}/posts`;
    const r = await fetch(url, {
      method, headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(form),
    });
    if (r.ok) onSave(await r.json());
    setSaving(false);
  };

  return (
    <form onSubmit={save} className="admin-form">
      <div className="admin-form-row">
        <label>Заголовок *</label>
        <input className="admin-input" value={form.title} onChange={set("title")} required />
      </div>
      <div className="admin-form-row">
        <label>Специальность</label>
        <SpecialtySelect value={form.specialty} onChange={set("specialty")} allowedSpecialties={allowedSpecialties} />
      </div>
      <div className="admin-form-row">
        <label>Теги (через ;)</label>
        <input className="admin-input" value={form.tags || ""} onChange={set("tags")} placeholder="Кардиология; ЭКГ; АГ" />
      </div>
      <div className="admin-form-row">
        <label>Содержание * <span style={{ fontWeight: 400, color: "#888", fontSize: "0.78rem" }}>(поддерживается Markdown)</span></label>
        <MarkdownEditor value={form.content} onChange={set("content")} required rows={10} token={token} />
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button type="submit" disabled={saving} style={{ marginTop: 0 }}>{saving ? "Сохраняем…" : "Сохранить"}</button>
        <button type="button" onClick={onCancel} style={{ background: "#888", marginTop: 0 }}>Отмена</button>
      </div>
    </form>
  );
}

// ── Форма лекции ─────────────────────────────────────────────────────────

const MATERIAL_TYPES = ["Лекция", "Мастер-класс", "Вебинар", "Материал"];

function LectureForm({ initial, onSave, onCancel, token, allowedSpecialties }) {
  const [form, setForm] = useState(initial || { title: "", description: "", video_url: "", duration: "", specialty: allowedSpecialties?.[0] || SPECIALTIES[0].id, material_type: "Лекция" });
  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }));
  const [saving, setSaving] = useState(false);

  const save = async e => {
    e.preventDefault();
    setSaving(true);
    const method = initial?.id ? "PUT" : "POST";
    const url = initial?.id ? `${API_URL}/lectures/${initial.id}` : `${API_URL}/lectures`;
    const r = await fetch(url, {
      method, headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(form),
    });
    if (r.ok) onSave(await r.json());
    setSaving(false);
  };

  return (
    <form onSubmit={save} className="admin-form">
      <div className="admin-form-row">
        <label>Название *</label>
        <input className="admin-input" value={form.title} onChange={set("title")} required />
      </div>
      <div className="admin-form-row">
        <label>Специальность</label>
        <SpecialtySelect value={form.specialty} onChange={set("specialty")} allowedSpecialties={allowedSpecialties} />
      </div>
      <div className="admin-form-row">
        <label>Тип материала</label>
        <select className="admin-input" value={form.material_type || ""} onChange={set("material_type")}>
          {MATERIAL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      <div className="admin-form-row">
        <label>Ссылка на видео</label>
        <input className="admin-input" value={form.video_url || ""} onChange={set("video_url")} placeholder="YouTube: https://youtube.com/watch?v=..." />
        <span style={{ fontSize: "0.78rem", color: "#888", marginTop: 4, display: "block" }}>
          Для VK Видео: в плеере нажмите «Поделиться → Встроить» и вставьте сюда весь код
          (&lt;iframe src="vk.com/video_ext.php?...hash=..."&gt;) целиком — обычная ссылка на видео
          для встраивания не подойдёт, там нет нужного токена доступа.
        </span>
      </div>
      <div className="admin-form-row">
        <label>Длительность</label>
        <input className="admin-input" value={form.duration || ""} onChange={set("duration")} placeholder="45 мин" />
      </div>
      <div className="admin-form-row">
        <label>Описание <span style={{ fontWeight: 400, color: "#888", fontSize: "0.78rem" }}>(поддерживается Markdown)</span></label>
        <MarkdownEditor value={form.description || ""} onChange={set("description")} rows={7} token={token} />
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button type="submit" disabled={saving} style={{ marginTop: 0 }}>{saving ? "Сохраняем…" : "Сохранить"}</button>
        <button type="button" onClick={onCancel} style={{ background: "#888", marginTop: 0 }}>Отмена</button>
      </div>
    </form>
  );
}

// ── Форма события ────────────────────────────────────────────────────────

function EventForm({ initial, onSave, onCancel, token, allowedSpecialties }) {
  const toInput = iso => iso ? iso.slice(0, 16) : "";
  const [form, setForm] = useState(initial
    ? { ...initial, date: toInput(initial.date) }
    : { title: "", description: "", date: "", location: "", event_type: "Конференция", specialty: allowedSpecialties?.[0] || SPECIALTIES[0].id }
  );
  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }));
  const [saving, setSaving] = useState(false);

  const save = async e => {
    e.preventDefault();
    setSaving(true);
    const method = initial?.id ? "PUT" : "POST";
    const url = initial?.id ? `${API_URL}/events/${initial.id}` : `${API_URL}/events`;
    const r = await fetch(url, {
      method, headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ ...form, date: new Date(form.date).toISOString() }),
    });
    if (r.ok) onSave(await r.json());
    setSaving(false);
  };

  return (
    <form onSubmit={save} className="admin-form">
      <div className="admin-form-row">
        <label>Название *</label>
        <input className="admin-input" value={form.title} onChange={set("title")} required />
      </div>
      <div className="admin-form-row admin-form-grid-2">
        <div>
          <label>Дата и время *</label>
          <input type="datetime-local" className="admin-input" value={form.date} onChange={set("date")} required />
        </div>
        <div>
          <label>Тип события</label>
          <select className="admin-input" value={form.event_type || ""} onChange={set("event_type")}>
            {["Конференция", "Вебинар", "Мастер-класс", "Школа", "Симпозиум"].map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="admin-form-row">
        <label>Место проведения</label>
        <input className="admin-input" value={form.location || ""} onChange={set("location")} placeholder="г. Томск, ТНИМЦ" />
      </div>
      <div className="admin-form-row">
        <label>Специальность</label>
        <SpecialtySelect value={form.specialty} onChange={set("specialty")} allowedSpecialties={allowedSpecialties} />
      </div>
      <div className="admin-form-row">
        <label>Описание</label>
        <textarea className="admin-textarea" value={form.description || ""} onChange={set("description")} rows={4} />
      </div>
      <div className="admin-form-row">
        <label>Ссылка на источник <span style={{ fontWeight: 400, color: "#888" }}>(необязательно)</span></label>
        <input className="admin-input" value={form.source_url || ""} onChange={set("source_url")} placeholder="https://..." />
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button type="submit" disabled={saving} style={{ marginTop: 0 }}>{saving ? "Сохраняем…" : "Сохранить"}</button>
        <button type="button" onClick={onCancel} style={{ background: "#888", marginTop: 0 }}>Отмена</button>
      </div>
    </form>
  );
}

// ── Форма клинического случая ─────────────────────────────────────────────

// ── Редактор интерактивного разбора (квиза) для кейса ─────────────────────

function CaseQuizEditor({ caseId, token }) {
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasQuiz, setHasQuiz] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    fetch(`${API_URL}/admin/cases/${caseId}/quiz`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setSteps(data.steps.map(s => ({
            question_text: s.question_text,
            options: s.options.map(o => ({ option_text: o.option_text, is_correct: o.is_correct, feedback_text: o.feedback_text || "" })),
          })));
          setHasQuiz(true);
        }
      })
      .finally(() => setLoading(false));
  }, [caseId, token]);

  const addStep = () => setSteps(s => [...s, {
    question_text: "",
    options: [{ option_text: "", is_correct: true, feedback_text: "" }, { option_text: "", is_correct: false, feedback_text: "" }],
  }]);
  const removeStep = idx => setSteps(s => s.filter((_, i) => i !== idx));
  const updateStep = (idx, value) => setSteps(s => s.map((step, i) => i === idx ? { ...step, question_text: value } : step));
  const addOption = stepIdx => setSteps(s => s.map((step, i) => i === stepIdx ? { ...step, options: [...step.options, { option_text: "", is_correct: false, feedback_text: "" }] } : step));
  const removeOption = (stepIdx, optIdx) => setSteps(s => s.map((step, i) => i === stepIdx ? { ...step, options: step.options.filter((_, j) => j !== optIdx) } : step));
  const updateOption = (stepIdx, optIdx, field, value) => setSteps(s => s.map((step, i) => i === stepIdx ? { ...step, options: step.options.map((o, j) => j === optIdx ? { ...o, [field]: value } : o) } : step));
  const setCorrect = (stepIdx, optIdx) => setSteps(s => s.map((step, i) => i === stepIdx ? { ...step, options: step.options.map((o, j) => ({ ...o, is_correct: j === optIdx })) } : step));

  const save = async () => {
    setSaving(true);
    setMsg("");
    const res = await fetch(`${API_URL}/admin/cases/${caseId}/quiz`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ steps }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) { setMsg("Сохранено"); setHasQuiz(true); }
    else setMsg(data.detail || "Ошибка сохранения");
    setSaving(false);
  };

  const deleteQuiz = async () => {
    if (!window.confirm("Удалить интерактивный разбор целиком?")) return;
    await fetch(`${API_URL}/admin/cases/${caseId}/quiz`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    setSteps([]);
    setHasQuiz(false);
    setMsg("");
  };

  if (loading) return null;

  return (
    <div style={{ marginTop: 8, marginBottom: 16, padding: 14, background: "#f7f8fc", borderRadius: 10, border: "1px solid #e2e4f0" }}>
      <div style={{ fontWeight: 700, color: "#007c84", marginBottom: 4 }}>Интерактивный разбор (квиз)</div>
      <p style={{ color: "#888", fontSize: "0.8rem", marginTop: 0, marginBottom: 10 }}>
        Необязательно. Если добавить шаги — на странице случая появится кнопка «Пройти разбор»:
        пошаговые вопросы с вариантами ответа и пояснением после каждого.
      </p>
      {steps.map((step, si) => (
        <div key={si} style={{ marginBottom: 14, padding: 10, background: "#fff", borderRadius: 8, border: "1px solid #e8eaf2" }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <input className="admin-input" placeholder={`Вопрос ${si + 1}`} value={step.question_text}
              onChange={e => updateStep(si, e.target.value)} style={{ flex: 1 }} />
            <button type="button" onClick={() => removeStep(si)} style={{ background: "#d9534f", marginTop: 0 }}>Удалить шаг</button>
          </div>
          {step.options.map((opt, oi) => (
            <div key={oi} style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6 }}>
              <input type="radio" name={`quiz-step-${si}`} checked={opt.is_correct} onChange={() => setCorrect(si, oi)} title="Правильный вариант" />
              <input className="admin-input" placeholder="Текст варианта" value={opt.option_text}
                onChange={e => updateOption(si, oi, "option_text", e.target.value)} style={{ flex: 1 }} />
              <input className="admin-input" placeholder="Пояснение (после ответа)" value={opt.feedback_text}
                onChange={e => updateOption(si, oi, "feedback_text", e.target.value)} style={{ flex: 1 }} />
              <button type="button" onClick={() => removeOption(si, oi)} style={{ background: "#888", padding: "4px 8px", marginTop: 0 }}>✕</button>
            </div>
          ))}
          <button type="button" onClick={() => addOption(si)} style={{ background: "#15803d", fontSize: "0.85rem", padding: "4px 10px", marginTop: 0 }}>+ вариант</button>
        </div>
      ))}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <button type="button" onClick={addStep} style={{ background: "#007c84", marginTop: 0 }}>+ шаг</button>
        <button type="button" onClick={save} disabled={saving || steps.length === 0} style={{ marginTop: 0 }}>
          {saving ? "Сохранение…" : "Сохранить разбор"}
        </button>
        {hasQuiz && <button type="button" onClick={deleteQuiz} style={{ background: "#d9534f", marginTop: 0 }}>Удалить весь разбор</button>}
        {msg && <span style={{ color: msg === "Сохранено" ? "#0d7a3a" : "#d9534f", fontSize: "0.85rem" }}>{msg}</span>}
      </div>
    </div>
  );
}

function CaseForm({ initial, onSave, onCancel, token, allowedSpecialties }) {
  const [form, setForm] = useState(initial || {
    title: "", content: "", tags: "", specialty: allowedSpecialties?.[0] || SPECIALTIES[0].id,
    status: "open", is_anonymous: false,
  });
  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }));
  const setCheck = f => e => setForm(p => ({ ...p, [f]: e.target.checked }));
  const [saving, setSaving] = useState(false);

  const save = async e => {
    e.preventDefault();
    setSaving(true);
    const method = initial?.id ? "PUT" : "POST";
    const url = initial?.id ? `${API_URL}/cases/${initial.id}` : `${API_URL}/cases`;
    const r = await fetch(url, {
      method, headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(form),
    });
    if (r.ok) onSave(await r.json());
    setSaving(false);
  };

  return (
    <form onSubmit={save} className="admin-form">
      <div style={{ background: "#fff1f6", border: "1.5px solid #e5005b", borderRadius: 10, padding: "10px 16px", marginBottom: 14, fontSize: "0.92rem", color: "#9c0040" }}>
        Персональные данные пациентов должны быть удалены перед публикацией. Для анонимизации файлов используйте раздел «Инструменты».
      </div>
      <div className="admin-form-row">
        <label>Заголовок *</label>
        <input className="admin-input" value={form.title} onChange={set("title")} required />
      </div>
      <div className="admin-form-row admin-form-grid-2">
        <div>
          <label>Специальность</label>
          <SpecialtySelect value={form.specialty} onChange={set("specialty")} allowedSpecialties={allowedSpecialties} />
        </div>
        <div>
          <label>Статус</label>
          <select className="admin-input" value={form.status || "open"} onChange={set("status")}>
            <option value="open">Обсуждается</option>
            <option value="resolved">Решён</option>
          </select>
        </div>
      </div>
      <div className="admin-form-row">
        <label>Теги (через ;)</label>
        <input className="admin-input" value={form.tags || ""} onChange={set("tags")} placeholder="Кардиология; ЭКГ; блокада" />
      </div>
      <div className="admin-form-row">
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={!!form.is_anonymous}
            onChange={setCheck("is_anonymous")}
            style={{ width: 16, height: 16 }}
          />
          Анонимная публикация (автор не отображается)
        </label>
      </div>
      <div className="admin-form-row">
        <label>Описание случая * <span style={{ fontWeight: 400, color: "#888", fontSize: "0.78rem" }}>(поддерживается Markdown)</span></label>
        <MarkdownEditor value={form.content} onChange={set("content")} required rows={12} token={token} />
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button type="submit" disabled={saving} style={{ marginTop: 0 }}>{saving ? "Сохраняем…" : "Сохранить"}</button>
        <button type="button" onClick={onCancel} style={{ background: "#888", marginTop: 0 }}>Отмена</button>
      </div>

      {initial?.id && <CaseQuizEditor caseId={initial.id} token={token} />}
    </form>
  );
}

// ── Управление пользователями ─────────────────────────────────────────────

function UsersTab({ token, isAdmin, currentUserId }) {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  useEffect(() => {
    fetch(`${API_URL}/auth/users`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(setUsers);
  }, [token]);

  const setRole = async (userId, role, specialty = null) => {
    await fetch(`${API_URL}/auth/users/${userId}/role`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ role, specialty }),
    });
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, role, specialty } : u));
  };

  const editContact = async u => {
    const email = window.prompt(`Почта для «${u.display_name}» (сейчас: ${u.email}).
Оставьте как есть, чтобы не менять:`, u.email);
    if (email === null) return;
    const name = window.prompt("Имя (ФИО). Автор в уже опубликованных материалах переименуется вместе с ним:", u.display_name);
    if (name === null) return;
    const body = {};
    if (email.trim() && email.trim().toLowerCase() !== u.email) body.email = email.trim();
    if (name.trim() && name.trim() !== u.display_name) body.display_name = name.trim();
    if (!Object.keys(body).length) return;
    const r = await fetch(`${API_URL}/auth/users/${u.id}/contact`, {
      method: "PATCH", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify(body),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { window.alert(d.detail || "Не удалось сохранить"); return; }
    setUsers(prev => prev.map(x => x.id === u.id ? { ...x, email: d.email, display_name: d.display_name } : x));
  };

  const setActive = async (userId, isActive) => {
    const r = await fetch(`${API_URL}/auth/users/${userId}/active`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ is_active: isActive }),
    });
    if (r.ok) {
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_active: isActive } : u));
    }
  };

  const q = search.trim().toLowerCase();
  const visible = q
    ? users.filter(u => u.display_name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
    : users;

  return (
    <div>
      <h3 className="admin-section-title">Пользователи портала</h3>
      <input
        value={search} onChange={e => setSearch(e.target.value)}
        placeholder="Поиск по имени или email…"
        className="admin-input" style={{ maxWidth: 320, marginBottom: 12 }}
      />
      <div className="admin-table-wrap">
      <table className="admin-table">
        <thead>
          <tr><th>ФИО</th><th>Email</th><th>Роль</th><th>Специальность</th><th>Регистрация</th><th>Статус</th><th>Действия</th></tr>
        </thead>
        <tbody>
          {visible.map(u => (
            <tr key={u.id} style={{ opacity: u.is_active === false ? 0.55 : 1 }}>
              <td>{u.display_name}</td>
              <td style={{ fontSize: "0.85rem", color: "#555" }}>{u.email}</td>
              <td><span className={`role-badge role-${u.role}`}>{u.role}</span></td>
              <td style={{ fontSize: "0.85rem" }}>
                {(u.specialty || "").split(";").map(s => s.trim()).filter(Boolean)
                  .map(id => SPECIALTIES.find(s => s.id === id)?.label || id)
                  .join(", ") || "—"}
              </td>
              <td style={{ fontSize: "0.82rem", color: "#888" }}>
                {u.created_at ? new Date(u.created_at).toLocaleDateString("ru") : "—"}
              </td>
              <td style={{ fontSize: "0.82rem" }}>
                {u.is_active === false
                  ? <span style={{ color: "#c0392b", fontWeight: 700 }}>заблокирован</span>
                  : <span style={{ color: "#15803d" }}>активен</span>}
              </td>
              <td>
                {isAdmin && u.role !== "admin" && (
                  <span style={{ display: "inline-flex", gap: 6, flexWrap: "wrap" }}>
                    <select style={{ fontSize: "0.82rem", padding: "3px 6px", borderRadius: 6, border: "1px solid #c4ceda" }}
                      value={u.role}
                      onChange={e => setRole(u.id, e.target.value, u.specialty)}>
                      <option value="user">user</option>
                      <option value="editor">editor</option>
                      <option value="admin">admin</option>
                    </select>
                    {u.role === "editor" && (
                      <SpecialtyMultiSelect
                        value={u.specialty}
                        onChange={newSpecialty => setRole(u.id, u.role, newSpecialty)}
                      />
                    )}
                    <button type="button" onClick={() => editContact(u)}
                      style={{ background: "var(--gray-500)", color: "#fff", border: "none", borderRadius: 6, padding: "3px 10px", fontSize: "0.8rem", cursor: "pointer" }}>
                      Почта / имя
                    </button>
                    {u.id !== currentUserId && (
                      <button type="button"
                        onClick={() => setActive(u.id, u.is_active === false)}
                        style={{ background: u.is_active === false ? "#15803d" : "#c0392b", color: "#fff",
                          border: "none", borderRadius: 6, padding: "3px 10px", fontSize: "0.8rem", cursor: "pointer" }}>
                        {u.is_active === false ? "Разблокировать" : "Заблокировать"}
                      </button>
                    )}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

// ── Обзор портала (дашборд, только admin) ─────────────────────────────────

function DashboardTab({ token }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(setStats);
  }, [token]);

  if (!stats) return <p style={{ color: "#888" }}>Загрузка…</p>;

  const specLabel = id => SPECIALTIES.find(s => s.id === id)?.label || id;
  const totalPosts = stats.content_by_specialty.reduce((s, c) => s + c.posts, 0);
  const totalLectures = stats.content_by_specialty.reduce((s, c) => s + c.lectures, 0);
  const totalCases = stats.content_by_specialty.reduce((s, c) => s + c.cases, 0);

  const Tile = ({ label, value, color }) => (
    <div className="card" style={{ minWidth: 140, textAlign: "center", padding: "16px 12px" }}>
      <div style={{ fontSize: "1.7rem", fontWeight: 800, color: color || "#007c84" }}>{value}</div>
      <div style={{ fontSize: "0.82rem", color: "#888", marginTop: 2 }}>{label}</div>
    </div>
  );

  return (
    <div>
      <h3 className="admin-section-title">Обзор портала</h3>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
        <Tile label="Пользователей" value={stats.users.total} />
        <Tile label="Заблокировано" value={stats.users.blocked} color={stats.users.blocked > 0 ? "#c0392b" : "#15803d"} />
        <Tile label="Заявок на верификацию" value={stats.verification_pending} color={stats.verification_pending > 0 ? "#d97706" : "#15803d"} />
        <Tile label="Жалоб в очереди" value={stats.reports_pending} color={stats.reports_pending > 0 ? "#c0392b" : "#15803d"} />
        <Tile label="Статей" value={totalPosts} />
        <Tile label="Материалов" value={totalLectures} />
        <Tile label="Клин. случаев" value={totalCases} />
        <Tile label="Каналов чата" value={stats.channels_total} />
        <Tile label="Комментариев" value={stats.comments_total} />
        <Tile label="Событий" value={stats.events_total} />
      </div>

      <h4 style={{ color: "#007c84", marginBottom: 8 }}>Пользователи по ролям</h4>
      <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
        {Object.entries(stats.users.by_role).map(([role, count]) => (
          <span key={role} className={`role-badge role-${role}`} style={{ fontSize: "0.85rem" }}>
            {role}: {count}
          </span>
        ))}
      </div>

      <h4 style={{ color: "#007c84", marginBottom: 8 }}>Контент по специальностям</h4>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr><th>Специальность</th><th>Статьи</th><th>Материалы</th><th>Случаи</th></tr>
          </thead>
          <tbody>
            {stats.content_by_specialty.map(c => (
              <tr key={c.specialty}>
                <td>{specLabel(c.specialty)}</td>
                <td>{c.posts}</td>
                <td>{c.lectures}</td>
                <td style={{ color: c.cases === 0 ? "#c0392b" : "inherit" }}>{c.cases}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Пробелы ИИ-поиска — что ищут врачи, но не находят (только admin) ──────

function SearchGapsTab({ token }) {
  const [gaps, setGaps] = useState(null);
  const [days, setDays] = useState(30);

  const load = useCallback(() => {
    setGaps(null);
    fetch(`${API_URL}/admin/search-gaps?days=${days}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(setGaps);
  }, [token, days]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <h3 className="admin-section-title">Пробелы ИИ-поиска</h3>
      <p style={{ color: "#555", marginBottom: 16, maxWidth: 640 }}>
        Запросы, на которые ИИ-поиск не смог ответить материалами портала —
        то есть либо ничего не нашлось, либо найденное оказалось нерелевантным.
        Частые повторяющиеся запросы здесь — подсказка, какую статью, лекцию
        или запись в справочник лекарств стоит добавить.
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {[7, 30, 90].map(d => (
          <button key={d} onClick={() => setDays(d)}
            className={`admin-tab-btn${days === d ? " active" : ""}`} style={{ fontSize: "0.85rem" }}>
            {d} дней
          </button>
        ))}
      </div>

      {gaps === null ? (
        <p style={{ color: "#888" }}>Загрузка…</p>
      ) : gaps.length === 0 ? (
        <p style={{ color: "#15803d" }}>Пробелов не найдено — все запросы за период получили ответ с источниками.</p>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr><th>Запрос</th><th>Сколько раз</th><th>Последний раз</th></tr>
            </thead>
            <tbody>
              {gaps.map(g => (
                <tr key={g.query_text}>
                  <td>{g.query_text}</td>
                  <td style={{ fontWeight: 700, color: g.count >= 3 ? "#c0392b" : "inherit" }}>{g.count}</td>
                  <td style={{ color: "#888" }}>{new Date(g.last_at).toLocaleDateString("ru-RU")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Производители оборудования (только admin) ─────────────────────────────

function ManufacturersTab({ token }) {
  const [items, setItems] = useState(null);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", website: "", logo_url: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [repPicker, setRepPicker] = useState({});   // { [manufacturerId]: {user_id, job_title} }

  const load = useCallback(() => {
    fetch(`${API_URL}/admin/manufacturers`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(setItems);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    fetch(`${API_URL}/auth/users`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(setUsers);
  }, [token]);

  const create = async e => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await fetch(`${API_URL}/admin/manufacturers`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: form.name.trim(),
          description: form.description.trim() || null,
          website: form.website.trim() || null,
          logo_url: form.logo_url.trim() || null,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || "Не удалось создать");
      setForm({ name: "", description: "", website: "", logo_url: "" });
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = (m) => {
    fetch(`${API_URL}/admin/manufacturers/${m.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ is_active: !m.is_active }),
    }).then(() => load());
  };

  const addRep = (manufacturerId) => {
    const picked = repPicker[manufacturerId];
    if (!picked?.user_id) return;
    fetch(`${API_URL}/admin/manufacturers/${manufacturerId}/reps`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ user_id: Number(picked.user_id), job_title: picked.job_title || null }),
    }).then(r => {
      if (r.ok) { setRepPicker(p => ({ ...p, [manufacturerId]: { user_id: "", job_title: "" } })); load(); }
    });
  };

  const removeRep = (manufacturerId, userId) => {
    fetch(`${API_URL}/admin/manufacturers/${manufacturerId}/reps/${userId}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    }).then(() => load());
  };

  if (items === null) return <p style={{ color: "#888" }}>Загрузка…</p>;

  return (
    <div>
      <h3 className="admin-section-title">Производители оборудования</h3>
      <p style={{ color: "#555", marginBottom: 16, maxWidth: 680 }}>
        Раздел работает как поддержка: врачи задают технические вопросы, назначенный
        представитель отвечает публично от имени компании. Отвечать может только тот,
        кого вы здесь назначили представителем.
      </p>

      <form onSubmit={create} className="card" style={{ marginBottom: 24 }}>
        <div style={{ fontWeight: 700, marginBottom: 10, color: "#007c84" }}>Добавить производителя</div>
        {error && <div className="auth-error" style={{ marginBottom: 10 }}>{error}</div>}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
          <input type="text" value={form.name} required placeholder="Название (Schiller, Инкарт…)"
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            style={{ flex: 1, minWidth: 180, padding: "7px 10px", borderRadius: 6, border: "1px solid #ccc" }} />
          <input type="text" value={form.website} placeholder="Сайт (https://…)"
            onChange={e => setForm(f => ({ ...f, website: e.target.value }))}
            style={{ flex: 1, minWidth: 180, padding: "7px 10px", borderRadius: 6, border: "1px solid #ccc" }} />
        </div>
        <input type="text" value={form.logo_url} placeholder="URL логотипа (необязательно)"
          onChange={e => setForm(f => ({ ...f, logo_url: e.target.value }))}
          style={{ width: "100%", padding: "7px 10px", borderRadius: 6, border: "1px solid #ccc", marginBottom: 10, boxSizing: "border-box" }} />
        <textarea value={form.description} rows={2} placeholder="Чем занимается, линейка приборов"
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          style={{ width: "100%", padding: "7px 10px", borderRadius: 6, border: "1px solid #ccc", marginBottom: 10, boxSizing: "border-box" }} />
        <button type="submit" disabled={busy || !form.name.trim()}>{busy ? "Создаём…" : "Создать"}</button>
      </form>

      {items.length === 0 ? (
        <p style={{ color: "#888" }}>Производители ещё не добавлены.</p>
      ) : (
        items.map(m => {
          const assignedIds = new Set(m.reps.map(r => r.user_id));
          const available = users.filter(u => !assignedIds.has(u.id));
          const picked = repPicker[m.id] || { user_id: "", job_title: "" };
          return (
            <div key={m.id} className="card" style={{ marginBottom: 12, opacity: m.is_active ? 1 : 0.6 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                <div>
                  <strong>{m.name}</strong>
                  <span style={{ color: "#999", fontSize: "0.82rem", marginLeft: 8 }}>/{m.slug}</span>
                  {!m.is_active && <span style={{ marginLeft: 8, color: "#c0392b", fontSize: "0.8rem", fontWeight: 700 }}>скрыт</span>}
                  {m.website && (
                    <div style={{ fontSize: "0.82rem", color: "#888" }}>{m.website}</div>
                  )}
                </div>
                <button onClick={() => toggleActive(m)}
                  style={{ background: m.is_active ? "#d97706" : "#15803d", padding: "4px 12px", marginTop: 0, fontSize: "0.82rem" }}>
                  {m.is_active ? "Скрыть" : "Показать"}
                </button>
              </div>

              <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid #eef0f6" }}>
                <div style={{ fontSize: "0.82rem", color: "#555", fontWeight: 600, marginBottom: 6 }}>
                  Представители
                </div>
                {m.reps.length === 0 ? (
                  <div style={{ color: "#c4004f", fontSize: "0.85rem", marginBottom: 8 }}>
                    не назначены — отвечать на вопросы пока некому
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 8 }}>
                    {m.reps.map(r => (
                      <span key={r.user_id} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.86rem" }}>
                        <span style={{ fontWeight: 700, color: "#15803d" }}>{r.display_name}</span>
                        {r.job_title && <span style={{ color: "#888", fontSize: "0.8rem" }}>{r.job_title}</span>}
                        <button onClick={() => removeRep(m.id, r.user_id)} title="Снять"
                          style={{ background: "none", border: "none", color: "#c0392b", cursor: "pointer", padding: 0, marginTop: 0 }}>
                          ✕
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <select value={picked.user_id}
                    onChange={e => setRepPicker(p => ({ ...p, [m.id]: { ...picked, user_id: e.target.value } }))}
                    style={{ padding: "4px 8px", borderRadius: 6, border: "1px solid #ccc", fontSize: "0.85rem" }}>
                    <option value="">— выбрать пользователя —</option>
                    {available.map(u => <option key={u.id} value={u.id}>{u.display_name}</option>)}
                  </select>
                  <input type="text" value={picked.job_title} placeholder="должность"
                    onChange={e => setRepPicker(p => ({ ...p, [m.id]: { ...picked, job_title: e.target.value } }))}
                    style={{ padding: "4px 8px", borderRadius: 6, border: "1px solid #ccc", fontSize: "0.85rem", width: 170 }} />
                  <button onClick={() => addRep(m.id)} disabled={!picked.user_id}
                    style={{ padding: "4px 12px", marginTop: 0, fontSize: "0.83rem" }}>
                    Назначить
                  </button>
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

// ── Ответственные редакторы разделов (только admin) ─────────────────────────────

const APP_STATUS_LABEL = { new: "Новый", accepted: "Принят", rejected: "Отклонён" };

function SpecialtyEditorsTab({ token }) {
  const [editors, setEditors] = useState(null);
  const [applications, setApplications] = useState([]);
  const [users, setUsers] = useState([]);
  const [picker, setPicker] = useState({});   // { [specialty]: выбранный userId в селекте добавления }

  const load = useCallback(() => {
    fetch(`${API_URL}/specialty-editors`)
      .then(r => r.ok ? r.json() : [])
      .then(setEditors);
    fetch(`${API_URL}/admin/specialty-editor-applications`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(setApplications);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    fetch(`${API_URL}/auth/users`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(setUsers);
  }, [token]);

  const addEditor = (specialty, userId) => {
    if (!userId) return;
    fetch(`${API_URL}/admin/specialty-editors/${specialty}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ user_id: Number(userId) }),
    }).then(r => {
      if (r.ok) { setPicker(p => ({ ...p, [specialty]: "" })); load(); }
    });
  };

  const removeEditor = (specialty, userId) => {
    fetch(`${API_URL}/admin/specialty-editors/${specialty}/${userId}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    }).then(() => load());
  };

  const decide = (id, status) => {
    fetch(`${API_URL}/admin/specialty-editor-applications/${id}?status=${status}`, {
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}` },
    }).then(() => load());
  };

  if (editors === null) return <p style={{ color: "#888" }}>Загрузка…</p>;

  const bySpecialty = {};
  for (const e of editors) {
    (bySpecialty[e.specialty] ||= []).push(e);
  }
  const newApps = applications.filter(a => a.status === "new");

  return (
    <div>
      <h3 className="admin-section-title">Ответственные редакторы разделов</h3>
      <p style={{ color: "#555", marginBottom: 16, maxWidth: 660 }}>
        У каждого раздела может быть один или несколько врачей, отвечающих за него публично — их
        имена видны на страницах специальности. Разделы без редактора показывают приглашение
        с формой отклика. Назначение автоматически выдаёт роль <b>editor</b> и добавляет специальность.
      </p>

      <div className="admin-table-wrap" style={{ marginBottom: 26 }}>
        <table className="admin-table">
          <thead>
            <tr><th>Раздел</th><th>Ответственные редакторы</th><th>Добавить</th></tr>
          </thead>
          <tbody>
            {SPECIALTIES.map(s => {
              const list = bySpecialty[s.id] || [];
              const assignedIds = new Set(list.map(e => e.editor_id));
              const available = users.filter(u => !assignedIds.has(u.id));
              return (
                <tr key={s.id}>
                  <td>{s.label}</td>
                  <td style={{ fontSize: "0.88rem" }}>
                    {list.length === 0 ? (
                      <span style={{ color: "#c4004f" }}>вакансия</span>
                    ) : (
                      <span style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {list.map(e => (
                          <span key={e.editor_id} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            <span style={{ fontWeight: 700, color: "#15803d" }}>{e.display_name}</span>
                            <button onClick={() => removeEditor(s.id, e.editor_id)}
                              title="Снять с раздела"
                              style={{ background: "none", border: "none", color: "#c0392b", cursor: "pointer", padding: 0, marginTop: 0, fontSize: "0.8rem" }}>
                              ✕
                            </button>
                          </span>
                        ))}
                      </span>
                    )}
                  </td>
                  <td>
                    <span style={{ display: "flex", gap: 6 }}>
                      <select
                        value={picker[s.id] || ""}
                        onChange={ev => setPicker(p => ({ ...p, [s.id]: ev.target.value }))}
                        style={{ padding: "4px 8px", borderRadius: 6, border: "1px solid #ccc", fontSize: "0.85rem" }}>
                        <option value="">— выбрать врача —</option>
                        {available.map(u => <option key={u.id} value={u.id}>{u.display_name}</option>)}
                      </select>
                      <button onClick={() => addEditor(s.id, picker[s.id])} disabled={!picker[s.id]}
                        style={{ padding: "4px 10px", marginTop: 0, fontSize: "0.83rem" }}>
                        Добавить
                      </button>
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <h3 className="admin-section-title">
        Отклики на вакансии{newApps.length > 0 && (
          <span style={{ marginLeft: 8, background: "#c0392b", color: "#fff", borderRadius: 999, padding: "1px 9px", fontSize: "0.8rem" }}>
            {newApps.length}
          </span>
        )}
      </h3>

      {applications.length === 0 ? (
        <p style={{ color: "#888" }}>Откликов пока нет.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {applications.map(a => {
            const specLabel = SPECIALTIES.find(s => s.id === a.specialty)?.label || a.specialty;
            return (
              <div key={a.id} className="card">
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                  <div>
                    <strong>{a.display_name}</strong>
                    {a.verification_status === "verified" && (
                      <span style={{ marginLeft: 6, color: "#15803d", fontSize: "0.8rem", fontWeight: 700 }}>подтверждён</span>
                    )}
                    <div style={{ color: "#888", fontSize: "0.82rem" }}>
                      {a.email} · на раздел «{specLabel}» · {new Date(a.created_at).toLocaleDateString("ru-RU")}
                    </div>
                  </div>
                  <span style={{ fontSize: "0.8rem", color: a.status === "new" ? "#c0392b" : "#555" }}>
                    {APP_STATUS_LABEL[a.status] || a.status}
                  </span>
                </div>
                {a.message && (
                  <p style={{ color: "#374151", fontSize: "0.9rem", marginTop: 8, marginBottom: 0, whiteSpace: "pre-wrap" }}>
                    {a.message}
                  </p>
                )}
                <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                  <button onClick={() => addEditor(a.specialty, a.user_id)}
                    style={{ background: "#15803d", padding: "5px 12px", marginTop: 0, fontSize: "0.83rem" }}>
                    Назначить редактором «{specLabel}»
                  </button>
                  {a.status !== "accepted" && (
                    <button onClick={() => decide(a.id, "accepted")}
                      style={{ background: "#007c84", padding: "5px 12px", marginTop: 0, fontSize: "0.83rem" }}>
                      Пометить принятым
                    </button>
                  )}
                  {a.status !== "rejected" && (
                    <button onClick={() => decide(a.id, "rejected")}
                      style={{ background: "#9ca3af", padding: "5px 12px", marginTop: 0, fontSize: "0.83rem" }}>
                      Отклонить
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Второе мнение по записям ЭКГ/Холтер/СМАД — триаж заявок (только admin) ──

const SO_MODALITY_LABEL = { ecg: "ЭКГ", holter: "Холтер", smad: "СМАД" };
const SO_STATUS_LABEL = {
  new: "Новая", in_review: "Назначен эксперт", scheduled: "Онлайн-разбор назначен", answered: "Есть заключение",
};

function SecondOpinionScheduleForm({ token, requestId, onScheduled }) {
  const [date, setDate] = useState("");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);

  const schedule = async () => {
    if (!date) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/admin/second-opinion/${requestId}/schedule`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ date: new Date(date).toISOString(), title: title || null }),
      });
      if (r.ok) onScheduled(await r.json());
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginTop: 8 }}>
      <input type="datetime-local" value={date} onChange={e => setDate(e.target.value)}
        style={{ padding: "5px 8px", borderRadius: 6, border: "1px solid #ccc", fontSize: "0.82rem" }} />
      <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Название события (необязательно)"
        style={{ padding: "5px 8px", borderRadius: 6, border: "1px solid #ccc", fontSize: "0.82rem", width: 200 }} />
      <button onClick={schedule} disabled={busy || !date} style={{ background: "#007c84", padding: "5px 12px", marginTop: 0, fontSize: "0.82rem" }}>
        {busy ? "…" : "Назначить онлайн-разбор"}
      </button>
    </div>
  );
}

function SecondOpinionTab({ token }) {
  const [requests, setRequests] = useState(null);
  const [experts, setExperts] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");

  const load = useCallback(() => {
    setRequests(null);
    const qs = statusFilter ? `?status=${statusFilter}` : "";
    fetch(`${API_URL}/admin/second-opinion${qs}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(setRequests);
  }, [token, statusFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    fetch(`${API_URL}/auth/users`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(users => setExperts(users.filter(u => u.verification_status === "verified")));
  }, [token]);

  const assign = (requestId, reviewerId) => {
    fetch(`${API_URL}/admin/second-opinion/${requestId}/assign`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ reviewer_id: reviewerId || null }),
    }).then(() => load());
  };

  return (
    <div>
      <h3 className="admin-section-title">Второе мнение — заявки на разбор записей</h3>

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {[["", "Все"], ["new", "Новые"], ["in_review", "В работе"], ["scheduled", "Назначен разбор"], ["answered", "Отвечено"]].map(([v, l]) => (
          <button key={v} onClick={() => setStatusFilter(v)}
            className={`admin-tab-btn${statusFilter === v ? " active" : ""}`} style={{ fontSize: "0.85rem" }}>
            {l}
          </button>
        ))}
      </div>

      {requests === null ? (
        <p style={{ color: "#888" }}>Загрузка…</p>
      ) : requests.length === 0 ? (
        <p style={{ color: "#888" }}>Заявок нет.</p>
      ) : (
        requests.map(req => (
          <div key={req.id} className="card" style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
              <div>
                <strong>{SO_MODALITY_LABEL[req.modality] || req.modality}</strong>
                {req.manufacturer && ` · ${req.manufacturer}`}
                <span style={{ color: "#888", fontSize: "0.82rem", marginLeft: 8 }}>от {req.requester_name}</span>
              </div>
              <span style={{ fontSize: "0.8rem", color: "#555" }}>{SO_STATUS_LABEL[req.status] || req.status}</span>
            </div>
            {req.description && <p style={{ color: "#374151", fontSize: "0.88rem", marginTop: 6 }}>{req.description}</p>}
            <div style={{ color: "#6b7280", fontSize: "0.82rem", marginTop: 4 }}>
              Файлов: {req.files.length} ({req.files.map(f => f.original_name).join(", ")})
            </div>

            <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10, flexWrap: "wrap" }}>
              <label style={{ fontSize: "0.82rem", color: "#555" }}>
                Эксперт:{" "}
                <select value={req.reviewer_name ? experts.find(e => e.display_name === req.reviewer_name)?.id || "" : ""}
                  onChange={e => assign(req.id, e.target.value ? Number(e.target.value) : null)}
                  style={{ padding: "4px 8px", borderRadius: 6, border: "1px solid #ccc" }}>
                  <option value="">— не назначен —</option>
                  {experts.map(u => <option key={u.id} value={u.id}>{u.display_name}</option>)}
                </select>
              </label>
            </div>

            {!req.jitsi_room && (
              <SecondOpinionScheduleForm token={token} requestId={req.id} onScheduled={load} />
            )}
            {req.jitsi_room && (
              <div style={{ marginTop: 8, fontSize: "0.85rem", color: "#007c84" }}>
                Онлайн-разбор: {req.event_date && new Date(req.event_date).toLocaleString("ru-RU")} —{" "}
                <a href={`https://meet.jit.si/${req.jitsi_room}`} target="_blank" rel="noopener noreferrer">комната Jitsi</a>
              </div>
            )}

            {req.conclusion && (
              <div style={{ marginTop: 10, background: "#fff1f6", borderRadius: 8, padding: "8px 12px", fontSize: "0.85rem", color: "#007c84" }}>
                <strong>Заключение:</strong> {req.conclusion}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

// ── Каналы чата — обзор по всем специальностям (только admin) ─────────────

function ChannelsTab({ token }) {
  const [channels, setChannels] = useState([]);

  const load = useCallback(() => {
    fetch(`${API_URL}/channels`)
      .then(r => r.ok ? r.json() : [])
      .then(setChannels);
  }, []);

  useEffect(() => { load(); }, [load]);

  const specLabel = id => id ? (SPECIALTIES.find(s => s.id === id)?.label || id) : "Общий (все специальности)";

  const remove = (id) => {
    if (!window.confirm("Удалить канал вместе с историей сообщений?")) return;
    fetch(`${API_URL}/channels/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (r.ok) load(); });
  };

  return (
    <div>
      <h3 className="admin-section-title">Каналы обсуждений — все специальности</h3>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr><th>Название</th><th>Описание</th><th>Специальность</th><th>Действия</th></tr>
          </thead>
          <tbody>
            {channels.map(c => (
              <tr key={c.id}>
                <td>{c.label}</td>
                <td style={{ fontSize: "0.85rem", color: "#666" }}>{c.description}</td>
                <td style={{ fontSize: "0.85rem" }}>{specLabel(c.specialty)}</td>
                <td>
                  <ConfirmBtn label="Удалить" onConfirm={() => remove(c.id)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Telegram-каналы — автопубликация постов (только admin) ───────────────

function TelegramTab({ token }) {
  const [channels, setChannels] = useState([]);
  const [knownAuthors, setKnownAuthors] = useState([]);
  const [username, setUsername] = useState("");
  const [specialty, setSpecialty] = useState(SPECIALTIES[0].id);
  const [author, setAuthor] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [log, setLog] = useState(null);
  const [purgeCount, setPurgeCount] = useState(20);
  const [purging, setPurging] = useState(false);

  const load = useCallback(() => {
    fetch(`${API_URL}/admin/telegram-channels`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(setChannels);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    fetch(`${API_URL}/authors/known`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(setKnownAuthors);
  }, [token]);

  const specLabel = id => SPECIALTIES.find(s => s.id === id)?.label || id;

  const add = async e => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/admin/telegram-channels`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ username, specialty, author: author || null }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Не удалось подключить канал");
      setUsername("");
      setAuthor("");
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = id => {
    if (!window.confirm("Удалить канал вместе с настройками? Уже опубликованные посты останутся. Если нужно ещё и скрыть недавние посты — используйте «Отключить и скрыть посты» ниже.")) return;
    fetch(`${API_URL}/admin/telegram-channels/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (r.ok) load(); });
  };

  const toggle = id => {
    fetch(`${API_URL}/admin/telegram-channels/${id}/toggle`, { method: "PATCH", headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (r.ok) load(); });
  };

  const toggleLog = channelId => {
    if (expandedId === channelId) { setExpandedId(null); setLog(null); return; }
    setExpandedId(channelId);
    setLog(null);
    fetch(`${API_URL}/admin/telegram-channels/${channelId}/posts?limit=20`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(setLog);
  };

  const purgeRecent = async channelId => {
    const n = Math.max(1, Math.min(200, Number(purgeCount) || 20));
    if (!window.confirm(`Отключить канал и удалить с портала последние ${n} постов из него? Отменить нельзя.`)) return;
    setPurging(true);
    try {
      const r = await fetch(`${API_URL}/admin/telegram-channels/${channelId}/purge-recent`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ count: n }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || "Не удалось выполнить действие");
      window.alert(`Удалено постов: ${data.deleted}. Канал отключён.`);
      setExpandedId(null);
      setLog(null);
      load();
    } catch (err) {
      window.alert(err.message);
    } finally {
      setPurging(false);
    }
  };

  return (
    <div>
      <h3 className="admin-section-title">Telegram-каналы — автопубликация постов</h3>
      <p style={{ color: "#666", fontSize: "0.88rem", maxWidth: 640 }}>
        Владелец канала сначала должен добавить нашего бота в канал администратором.
        После этого подключите канал здесь по @username — новые посты будут появляться
        на портале автоматически, под указанной специальностью, без модерации.
      </p>

      <form onSubmit={add} className="card" style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 20 }}>
        <label style={{ fontSize: "0.85rem" }}>
          Username канала
          <input
            type="text" value={username} onChange={e => setUsername(e.target.value)}
            placeholder="@my_channel" required
            style={{ display: "block", padding: "7px 10px", borderRadius: 6, border: "1px solid #ccc", marginTop: 4 }}
          />
        </label>
        <label style={{ fontSize: "0.85rem" }}>
          Специальность
          <select value={specialty} onChange={e => setSpecialty(e.target.value)}
            style={{ display: "block", padding: "7px 10px", borderRadius: 6, border: "1px solid #ccc", marginTop: 4 }}>
            {SPECIALTIES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </label>
        <label style={{ fontSize: "0.85rem" }}>
          Автор (необязательно)
          <input
            type="text" value={author} onChange={e => setAuthor(e.target.value)}
            placeholder="Имя, отображаемое как автор" list="known-authors-list"
            style={{ display: "block", padding: "7px 10px", borderRadius: 6, border: "1px solid #ccc", marginTop: 4 }}
          />
          <datalist id="known-authors-list">
            {knownAuthors.map(a => <option key={a} value={a} />)}
          </datalist>
        </label>
        <button type="submit" disabled={busy}>{busy ? "Подключение…" : "Подключить канал"}</button>
      </form>

      {error && <div style={{ color: "#d9534f", marginBottom: 14, fontSize: "0.9rem" }}>{error}</div>}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr><th>Канал</th><th>Специальность</th><th>Автор</th><th>Статус</th><th>Действия</th></tr>
          </thead>
          <tbody>
            {channels.map(c => (
              <React.Fragment key={c.id}>
                <tr>
                  <td>{c.title || c.username} <span style={{ color: "#999", fontSize: "0.82rem" }}>@{c.username}</span></td>
                  <td style={{ fontSize: "0.85rem" }}>{specLabel(c.specialty)}</td>
                  <td style={{ fontSize: "0.85rem", color: "#666" }}>{c.author || "—"}</td>
                  <td style={{ fontSize: "0.85rem", color: c.is_active ? "#15803d" : "#c0392b" }}>
                    {c.is_active ? "Активен" : "Отключён"}
                  </td>
                  <td>
                    <span style={{ display: "inline-flex", gap: 6, flexWrap: "wrap" }}>
                      <button onClick={() => toggle(c.id)} style={{ background: c.is_active ? "#d97706" : "#15803d", padding: "4px 10px", marginTop: 0, fontSize: "0.82rem" }}>
                        {c.is_active ? "Приостановить" : "Возобновить"}
                      </button>
                      <button onClick={() => toggleLog(c.id)} style={{ background: "#007c84", padding: "4px 10px", marginTop: 0, fontSize: "0.82rem" }}>
                        {expandedId === c.id ? "Скрыть журнал" : "Журнал постов"}
                      </button>
                      <ConfirmBtn label="Удалить канал" onConfirm={() => remove(c.id)} />
                    </span>
                  </td>
                </tr>
                {expandedId === c.id && (
                  <tr>
                    <td colSpan={5} style={{ background: "#f8f9fc" }}>
                      <div style={{ padding: "10px 4px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                          <span style={{ fontSize: "0.85rem", color: "#555" }}>
                            Если канал начал публиковать не то — отключить его и разом скрыть с портала последние посты, пришедшие из него:
                          </span>
                          <input type="number" min={1} max={200} value={purgeCount}
                            onChange={e => setPurgeCount(e.target.value)}
                            style={{ width: 60, padding: "4px 6px", borderRadius: 6, border: "1px solid #ccc" }} />
                          <button onClick={() => purgeRecent(c.id)} disabled={purging}
                            style={{ background: "#d9534f", padding: "4px 12px", marginTop: 0, fontSize: "0.82rem" }}>
                            {purging ? "Выполняем…" : "Отключить и скрыть посты"}
                          </button>
                        </div>

                        {log === null ? (
                          <p style={{ color: "#888", fontSize: "0.85rem" }}>Загрузка журнала…</p>
                        ) : log.length === 0 ? (
                          <p style={{ color: "#888", fontSize: "0.85rem" }}>Из этого канала ещё ничего не опубликовано.</p>
                        ) : (
                          <ul style={{ margin: 0, paddingLeft: 18, fontSize: "0.85rem", color: "#333" }}>
                            {log.map(item => (
                              <li key={item.post_id}>
                                {item.title} <span style={{ color: "#999" }}>— {new Date(item.created_at).toLocaleString("ru-RU")}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {channels.length === 0 && (
              <tr><td colSpan={5} style={{ color: "#999", fontStyle: "italic" }}>Каналы ещё не подключены.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Очередь верификации врачей (только admin) ─────────────────────────────

function VerificationTab({ token }) {
  const [queue, setQueue] = useState([]);

  const load = useCallback(() => {
    fetch(`${API_URL}/auth/verification-queue`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(setQueue);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const decide = async (userId, status) => {
    await fetch(`${API_URL}/auth/users/${userId}/verification`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ status }),
    });
    setQueue(prev => prev.filter(u => u.id !== userId));
  };

  return (
    <div>
      <h3 className="admin-section-title">Заявки на верификацию врача</h3>
      {queue.length === 0 ? (
        <p style={{ color: "#999", fontStyle: "italic" }}>Заявок нет.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {queue.map(u => (
            <div key={u.id} className="card" style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
              <img
                src={`${u.verification_doc_url}?token=${token}`}
                alt="Документ"
                style={{ width: 160, height: 160, objectFit: "cover", borderRadius: 8, border: "1px solid #e0e0e0" }}
              />
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontWeight: 700, color: "#007c84" }}>{u.display_name}</div>
                <div style={{ fontSize: "0.85rem", color: "#888", marginBottom: 10 }}>{u.email}</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => decide(u.id, "verified")} style={{ background: "#15803d", marginTop: 0, display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <Check size={14} strokeWidth={2.4} /> Подтвердить
                  </button>
                  <button onClick={() => decide(u.id, "rejected")} style={{ background: "#d9534f", marginTop: 0, display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <X size={14} strokeWidth={2.4} /> Отклонить
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Жалобы / модерация (admin + editor) ───────────────────────────────────

function ReportsTab({ token }) {
  const [reports, setReports] = useState([]);

  const load = useCallback(() => {
    fetch(`${API_URL}/reports?status=pending`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(setReports);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const decide = async (id, status) => {
    await fetch(`${API_URL}/reports/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ status }),
    });
    setReports(prev => prev.filter(r => r.id !== id));
  };

  const CONTENT_LABEL = { comment: "Комментарий", post: "Статья", case: "Случай", lecture: "Материал" };

  return (
    <div>
      <h3 className="admin-section-title">Жалобы на рассмотрении</h3>
      {reports.length === 0 ? (
        <p style={{ color: "#999", fontStyle: "italic" }}>Жалоб нет.</p>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr><th>Тип</th><th>ID</th><th>Причина</th><th>Дата</th><th>Действия</th></tr>
            </thead>
            <tbody>
              {reports.map(r => (
                <tr key={r.id}>
                  <td>{CONTENT_LABEL[r.content_type] || r.content_type}</td>
                  <td>{r.content_id}</td>
                  <td style={{ fontSize: "0.88rem" }}>{r.reason || "—"}</td>
                  <td style={{ fontSize: "0.82rem", color: "#888" }}>{new Date(r.created_at).toLocaleDateString("ru")}</td>
                  <td style={{ display: "flex", gap: 6 }}>
                    <button onClick={() => decide(r.id, "resolved")} style={{ background: "#d9534f", padding: "4px 12px", marginTop: 0 }}>Удалить контент</button>
                    <button onClick={() => decide(r.id, "dismissed")} style={{ background: "#888", padding: "4px 12px", marginTop: 0 }}>Отклонить жалобу</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p style={{ fontSize: "0.82rem", color: "#999", marginTop: 10 }}>
        «Удалить контент» помечает жалобу решённой — сам комментарий/материал удалите
        вручную в соответствующем разделе после проверки.
      </p>
    </div>
  );
}

// ── Главная панель ────────────────────────────────────────────────────────

const ENDPOINT_MAP = { Статьи: "posts", Материалы: "lectures", События: "events", Случаи: "cases" };

export default function AdminPanel() {
  const { user, token, isEditor, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("Статьи");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);  // null | "new" | item object

  // Editor без прав admin привязан к одной или нескольким своим специальностям
  const allowedSpecialties = !isAdmin
    ? (user?.specialty || "").split(";").map(s => s.trim()).filter(Boolean)
    : null;
  const [filterSpec, setFilterSpec] = useState(allowedSpecialties?.[0] || "");

  const ENDPOINT = ENDPOINT_MAP[tab];

  const load = useCallback(() => {
    if (!ENDPOINT) return;
    const qs = filterSpec ? `?specialty=${filterSpec}` : "";
    fetch(`${API_URL}/${ENDPOINT}${qs}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(setItems);
  }, [ENDPOINT, filterSpec, token]);

  useEffect(() => { load(); }, [load]);

  if (!isEditor) {
    return (
      <section style={{ padding: 40, textAlign: "center" }}>
        <h2 className="section-title">Доступ закрыт</h2>
        <p>Раздел доступен только редакторам и администраторам.</p>
        <button onClick={() => navigate("/")}>← На главную</button>
      </section>
    );
  }

  if (!isAdmin && !user?.specialty) {
    return (
      <section style={{ padding: 40, textAlign: "center" }}>
        <h2 className="section-title">Специальность не назначена</h2>
        <p>Администратор ещё не привязал вас к специальности. Обратитесь к администратору портала.</p>
        <button onClick={() => navigate("/")}>← На главную</button>
      </section>
    );
  }

  const handleSave = saved => {
    setEditing(null);
    setItems(prev => {
      const idx = prev.findIndex(i => i.id === saved.id);
      return idx >= 0 ? prev.map(i => i.id === saved.id ? saved : i) : [saved, ...prev];
    });
  };

  const handleDelete = async (id) => {
    await fetch(`${API_URL}/${ENDPOINT}/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    setItems(prev => prev.filter(i => i.id !== id));
  };

  const renderForm = () => {
    const props = { initial: editing === "new" ? null : editing, onSave: handleSave, onCancel: () => setEditing(null), token, allowedSpecialties };
    if (tab === "Статьи") return <ArticleForm {...props} />;
    if (tab === "Материалы") return <LectureForm {...props} />;
    if (tab === "События") return <EventForm {...props} />;
    if (tab === "Случаи") return <CaseForm {...props} />;
    return null;
  };

  const ADMIN_ONLY_TABS = ["Обзор", "Пользователи", "Верификация", "Редакторы разделов", "Каналы", "Telegram", "ИИ-поиск", "Второе мнение", "Производители", "Баннеры", "ВКонтакте", "Серии", "Статистика", "Единый вход", "Импорт чата"];
  const visibleTabs = isAdmin ? TABS : TABS.filter(t => !ADMIN_ONLY_TABS.includes(t));

  return (
    <section>
      <h2 className="section-title">Управление контентом</h2>
      <p style={{ color: "#555", marginBottom: 16 }}>
        Вы вошли как <strong>{user?.display_name}</strong> · роль: <span className={`role-badge role-${user?.role}`}>{user?.role}</span>
      </p>

      {/* Вкладки */}
      <div className="admin-tabs">
        {visibleTabs.map(t => (
          <button key={t} onClick={() => { setTab(t); setEditing(null); }}
            className={`admin-tab-btn${tab === t ? " active" : ""}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === "Обзор" ? (
        <DashboardTab token={token} />
      ) : tab === "Пользователи" ? (
        <UsersTab token={token} isAdmin={isAdmin} currentUserId={user?.id} />
      ) : tab === "Верификация" ? (
        <VerificationTab token={token} />
      ) : tab === "Жалобы" ? (
        <ReportsTab token={token} />
      ) : tab === "Каналы" ? (
        <ChannelsTab token={token} />
      ) : tab === "Telegram" ? (
        <TelegramTab token={token} />
      ) : tab === "ИИ-поиск" ? (
        <SearchGapsTab token={token} />
      ) : tab === "Второе мнение" ? (
        <SecondOpinionTab token={token} />
      ) : tab === "Производители" ? (
        <ManufacturersTab token={token} />
      ) : tab === "Баннеры" ? (
        <BannersTab token={token} />
      ) : tab === "ВКонтакте" ? (
        <VkTab token={token} />
      ) : tab === "Серии" ? (
        <SeriesTab token={token} />
      ) : tab === "Статистика" ? (
        <StatsTab token={token} />
      ) : tab === "Импорт чата" ? (
        <ChatImportTab token={token} />
      ) : tab === "Единый вход" ? (
        <SsoTab token={token} />
      ) : tab === "Редакторы разделов" ? (
        <SpecialtyEditorsTab token={token} />
      ) : editing ? (
        <div className="admin-editor-box">
          <h3 className="admin-section-title">
            {editing === "new" ? `Новый элемент — ${tab}` : `Редактировать — ${tab}`}
          </h3>
          {renderForm()}
        </div>
      ) : (
        <>
          {/* Фильтр + кнопка добавить */}
          <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
            {allowedSpecialties && allowedSpecialties.length === 1 ? (
              <div className="admin-input" style={{ width: 200, background: "#f0f2f8", color: "#555", display: "flex", alignItems: "center" }}>
                {SPECIALTIES.find(s => s.id === allowedSpecialties[0])?.label || allowedSpecialties[0]}
              </div>
            ) : (
              <select className="admin-input" style={{ width: 200 }} value={filterSpec}
                onChange={e => setFilterSpec(e.target.value)}>
                {!allowedSpecialties && <option value="">Все специальности</option>}
                {(allowedSpecialties || SPECIALTIES.map(s => s.id)).map(id => {
                  const label = SPECIALTIES.find(s => s.id === id)?.label || id;
                  return <option key={id} value={id}>{label}</option>;
                })}
              </select>
            )}
            <button onClick={() => setEditing("new")} style={{ marginTop: 0, background: "#15803d" }}>
              + Добавить
            </button>
          </div>

          {/* Таблица */}
          {items.length === 0 ? (
            <p style={{ color: "#999", fontStyle: "italic" }}>Нет записей. Нажмите «+ Добавить».</p>
          ) : (
            <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Название</th>
                  <th>Специальность</th>
                  <th>Автор</th>
                  {tab === "Случаи" && <th>Статус</th>}
                  <th>Дата</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.id}>
                    <td style={{ fontWeight: 600 }}>
                      {item.title}
                      {item.is_anonymous && (
                        <span style={{ marginLeft: 6, fontSize: "0.75rem", color: "#888", background: "#f0f0f0", borderRadius: 4, padding: "1px 6px" }}>
                          анон.
                        </span>
                      )}
                    </td>
                    <td><span className="spec-badge">{item.specialty || "—"}</span></td>
                    <td style={{ fontSize: "0.85rem", color: "#555" }}>{item.author || "—"}</td>
                    {tab === "Случаи" && (
                      <td>
                        <span style={{
                          fontSize: "0.78rem", fontWeight: 600, borderRadius: 5,
                          padding: "2px 8px", color: "#fff",
                          background: item.status === "resolved" ? "#15803d" : "#007c84"
                        }}>
                          {item.status === "resolved" ? "Решён" : "Обсуждается"}
                        </span>
                      </td>
                    )}
                    <td style={{ fontSize: "0.82rem", color: "#888" }}>
                      {item.created_at ? new Date(item.created_at).toLocaleDateString("ru")
                        : (item.date ? new Date(item.date).toLocaleDateString("ru") : "—")}
                    </td>
                    <td style={{ display: "flex", gap: 6 }}>
                      <button onClick={() => setEditing(item)}
                        style={{ background: "#007c84", padding: "4px 12px", marginTop: 0 }}>
                        Изменить
                      </button>
                      <ConfirmBtn label="Удалить" onConfirm={() => handleDelete(item.id)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}
